import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchDocuments, fetchDocumentById } from '../data/documentSlice';

const Resources = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { items: documents, loading } = useSelector(state => state.documents);
  const [enrichedDocuments, setEnrichedDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Fetch documents on component mount
  useEffect(() => {
    if (documents.length === 0 && !loading) {
      dispatch(fetchDocuments());
    }
  }, [dispatch, documents.length, loading]);

  // Fetch full document data (including file) for each document
  useEffect(() => {
    const fetchFullDocuments = async () => {
      if (documents.length > 0) {
        setLoadingDocs(true);
        try {
          const fullDocs = await Promise.all(
            documents.map(doc =>
              dispatch(fetchDocumentById(doc.id)).then(result => {
                if (result.payload) {
                  return { ...doc, ...result.payload };
                }
                return doc;
              })
            )
          );
          setEnrichedDocuments(fullDocs.flat().filter(d => d));
          setLoadingDocs(false);
        } catch (error) {
          console.error('Error fetching full documents:', error);
          // Fallback to original documents
          setEnrichedDocuments(documents);
          setLoadingDocs(false);
        }
      }
    };

    fetchFullDocuments();
  }, [documents, dispatch]);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // Group documents by type
  const pdfDocuments = enrichedDocuments.filter(doc => doc.document_type === 'pdf');
  const imageDocuments = enrichedDocuments.filter(doc => doc.document_type === 'image');

  // Get file icon based on type
  const getFileIcon = (doc) => {
    if (doc.document_type === 'pdf') {
      return (
        <div className="w-full h-32 bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center rounded-t-lg">
          <svg className="w-16 h-16 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z"/>
          </svg>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white">
     
      {/* PDF Documents Section */}
      {pdfDocuments.length > 0 && (
        <section className="py-22 lg:py-32 bg-gray-50">
          <div className="container mx-auto px-2 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
             
              <div className="w-24 h-1 bg-black mx-auto" />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {pdfDocuments.map((doc) => (
                <motion.div
                  key={doc.id}
                  variants={fadeInUp}
                  className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full"
                >
                  {/* File Icon */}
                  {getFileIcon(doc)}

                  {/* Document Info */}
                  <div className="p-6 flex-grow flex flex-col">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition">
                      {doc.title}
                    </h3>
                    
                    <p className="text-xs text-gray-500 mb-4">
                      📅 {new Date(doc.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>

                    {doc.description && (
                      <p className="text-sm text-gray-600 mb-4 flex-grow">
                        {doc.description}
                      </p>
                    )}

                    {/* Download Button */}
                    <a
                      href={doc.fileData}
                      download={`${doc.title}.pdf`}
                      className="mt-auto inline-block bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 text-center"
                    >
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('resources_page.download') || 'Download PDF'}
                      </span>
                    </a>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Image Documents Section */}
      {imageDocuments.length > 0 && (
        <section className="py-20 lg:py-32 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-3xl lg:text-4xl font-black mb-4 uppercase tracking-wider text-black">
                {t('resources_page.image_documents') || 'Image Resources'}
              </h2>
              <div className="w-24 h-1 bg-black mx-auto" />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {imageDocuments.map((doc) => (
                <motion.div
                  key={doc.id}
                  variants={fadeInUp}
                  className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden bg-gray-200">
                    <img
                      src={doc.fileData}
                      alt={doc.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all duration-300" />
                  </div>

                  {/* Overlay Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">
                      {doc.title}
                    </h3>
                    <p className="text-xs text-gray-200 mb-4">
                      📅 {new Date(doc.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <a
                      href={doc.fileData}
                      download={`${doc.title}.jpg`}
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 text-center text-sm"
                    >
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('resources_page.download') || 'Download'}
                      </span>
                    </a>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!loading && !loadingDocs && enrichedDocuments.length === 0 && (
        <section className="py-20 lg:py-32 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-4 text-xl font-bold text-gray-900">
                {t('resources_page.no_documents') || 'No Documents Available'}
              </h3>
              <p className="mt-2 text-gray-600">
                {t('resources_page.no_documents_desc') || 'Check back soon for available resources'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Loading State */}
      {(loading || loadingDocs) && (
        <section className="py-20 lg:py-32 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
            </div>
            <p className="mt-4 text-gray-600">{loadingDocs ? 'Loading document previews...' : 'Loading resources...'}</p>
          </div>
        </section>
      )}

     
    </div>
  );
};

export default Resources;
